import { Link } from 'react-router-dom';
import type { Game } from '../types';
import { Badge } from '../ui/badge';

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const fmtDuration = (sec?: number) => {
  if (!sec) return '';
  const m = Math.floor(sec / 60);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m} min`;
};

type StatusBadgeColor = 'zinc' | 'red' | 'amber' | 'lime' | 'rose';

const statusBadge = (s: Game['status']): { label: string; color: StatusBadgeColor } => {
  switch (s) {
    case 'SCHEDULED':
      return { label: 'Scheduled', color: 'zinc' };
    case 'LIVE':
      return { label: 'Live', color: 'red' };
    case 'RECORDING':
      return { label: 'Recording', color: 'amber' };
    case 'READY_FOR_REVIEW':
      return { label: 'Awaiting review', color: 'amber' };
    case 'READY':
      return { label: 'Ready', color: 'lime' };
    case 'FAILED':
      return { label: 'Failed', color: 'rose' };
  }
};

interface Props {
  game: Game;
  /** When provided, renders a team-scoped href so the public Game page can
   *  resolve team-specific channel/data. */
  teamId?: string;
}

export default function GameCard({ game, teamId }: Props) {
  const ready = game.status === 'READY';
  const status = statusBadge(game.status);
  const card = (
    <div className="group overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 transition hover:-translate-y-0.5 hover:border-emerald-500/40 hover:shadow-xl hover:shadow-emerald-950/20">
      <div className="relative aspect-video bg-[radial-gradient(circle_at_28%_24%,rgba(16,185,129,0.34),transparent_34%),linear-gradient(135deg,rgba(39,39,42,1),rgba(9,9,11,1))]">
        <div className="absolute left-4 top-4">
          <Badge color={status.color}>{status.label}</Badge>
        </div>
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-emerald-200/80">Replay</div>
            <div className="mt-1 text-2xl font-semibold text-white">{fmtDuration(game.duration) || 'Scheduled'}</div>
          </div>
          {ready ? <div className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white">Watch</div> : null}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-medium text-zinc-100">
          {game.homeOrAway === 'AWAY' ? '@' : 'vs'} {game.opponent || 'Game'}
          </h3>
          <span className="text-zinc-500 transition group-hover:translate-x-0.5 group-hover:text-emerald-300">→</span>
        </div>
        <p className="mt-2 text-sm text-zinc-400">
          {fmtDate(game.scheduledAt)}
          {game.venue ? ` · ${game.venue}` : ''}
        </p>
      </div>
    </div>
  );

  const href = teamId
    ? `/teams/${teamId}/games/${game.gameId}`
    : `/games/${game.gameId}`;

  return ready ? (
    <Link to={href}>{card}</Link>
  ) : (
    <div className="cursor-not-allowed opacity-70">{card}</div>
  );
}
